/**
 * @file SuppliersPage.tsx
 * @description Поставщики (phase08.5; phase19.31 — каркас справочника (б)
 * разбора B).
 *
 * Таблица от 1024: Название · ИНН · Контакт · Закупок · Состояние; на телефоне
 * — компактный список. Пустое состояние — по общему образцу (`ListEmpty`), а
 * не серой плашкой. Открытый на правку поставщик — в адресе `?edit=`.
 * «⋯»: изменить, включить/отключить, после линии — удалить с подтверждением;
 * если по поставщику есть закупочные цены, сервер не удаляет — предлагаем
 * отключить.
 */

import { Pencil, Plus, Power, Trash2, Truck } from "lucide-react";

import { SupplierEditDialog } from "../../../components/catalog/SupplierEditDialog";
import { useDirectoryList } from "../../../components/catalog/directory/useDirectoryList";
import { NEW_RECORD, useEditParam } from "../../../components/catalog/directory/useEditParam";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import type { ListColumn } from "../../../components/ui/responsive-list-types";
import type { RowAction } from "../../../components/ui/RowActionsMenu";
import { StatusPill } from "../../../components/ui/StatusPill";
import { useConfirm } from "../../../components/ui/use-confirm";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { catalogSuppliers, type CatalogSupplier } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import { directoryActiveMeta } from "../../../lib/catalog-status";

const contact = (s: CatalogSupplier) => [s.contactPhone, s.contactEmail].filter(Boolean).join(" · ") || "—";

const SUPPLIER_COLUMNS: ListColumn<CatalogSupplier>[] = [
  { key: "name", header: "Название", cell: (s) => <span className="font-medium">{s.displayName}</span>, compact: "title" },
  { key: "inn", header: "ИНН", width: "w-[148px]", cell: (s) => <span className="tabular-nums">{s.inn ?? "—"}</span>, compact: "meta", compactCell: (s) => (s.inn ? `ИНН ${s.inn}` : null) },
  { key: "contact", header: "Контакт", cell: (s) => <span className="text-on-surface-variant">{contact(s)}</span>, compact: "subtitle" },
  { key: "costs", header: "Закупок", width: "w-[104px]", align: "end", cell: (s) => <span className="tabular-nums">{s.costsCount ?? 0}</span> },
  { key: "state", header: "Состояние", width: "w-[140px]", cell: (s) => <StatusPill meta={directoryActiveMeta(s)} />, compact: "trailing" },
];

export function SuppliersPage() {
  const notify = useNotify();
  const list = useDirectoryList(catalogSuppliers.list, "Не удалось загрузить поставщиков");
  const edit = useEditParam();
  const { confirm, request, settle } = useConfirm();
  const editing = edit.editId && !edit.isNew ? (list.items.find((s) => s.id === edit.editId) ?? null) : null;

  async function run(work: () => Promise<unknown>, done: string, failed: string) {
    try {
      await work();
      notify.success(done);
      await list.refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : failed);
    }
  }

  async function remove(s: CatalogSupplier) {
    const ok = await confirm({ title: `Удалить поставщика «${s.displayName}»?`, action: "Удалить", danger: true });
    if (!ok) return;
    try {
      await catalogSuppliers.remove(s.id);
      notify.success("Поставщик удалён");
      await list.refresh();
    } catch (err) {
      if (!(err instanceof ApiError && err.code === "supplier_has_costs")) {
        notify.error(err instanceof ApiError ? err.message : "Не удалось удалить поставщика");
        return;
      }
      const off = await confirm({
        title: "Удалить нельзя — есть закупочные цены",
        description: "По поставщику записаны закупочные цены товаров. Отключить его? История останется.",
        action: "Отключить",
      });
      if (off) await run(() => catalogSuppliers.remove(s.id, true), "Поставщик отключён", "Не удалось отключить");
    }
  }

  const rowActions = (s: CatalogSupplier): RowAction[] => [
    { key: "edit", label: "Изменить", icon: Pencil, run: () => edit.open(s.id) },
    {
      key: "toggle",
      label: s.isActive ? "Отключить" : "Включить",
      icon: Power,
      run: () => void run(() => catalogSuppliers.update(s.id, { isActive: !s.isActive }), s.isActive ? "Поставщик отключён" : "Поставщик включён", "Не удалось изменить"),
    },
    { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => void remove(s) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Поставщики"
        subtitle="У кого закупаете товар и сырьё — для учёта себестоимости"
        action={
          <Button variant="brand" onClick={() => edit.open(NEW_RECORD)}>
            <Plus className="size-4" />
            Поставщик
          </Button>
        }
      />
      <ResponsiveList
        aria-label="Поставщики"
        rows={list.items}
        columns={SUPPLIER_COLUMNS}
        rowKey={(s) => s.id}
        rowHref={(s) => edit.hrefFor(s.id)}
        rowLabel={(s) => `Поставщик ${s.displayName}`}
        rowActions={rowActions}
        rowActionsTitle={(s) => `Поставщик «${s.displayName}»`}
        loading={list.loading}
        error={list.error}
        onRetry={() => void list.refresh()}
        empty={{ icon: Truck, title: "Поставщиков пока нет", description: "Добавьте поставщика — его можно указать в закупочной цене товара" }}
      />
      {edit.isNew || editing ? (
        <SupplierEditDialog
          open
          supplier={editing}
          onClose={edit.close}
          onSaved={() => {
            edit.close();
            void list.refresh();
          }}
        />
      ) : null}
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
